
import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, Info, Trash, Plus, X, ListChecks, ChevronDown, ChevronUp, Users, Mail, Package, GitBranch } from 'lucide-react';
import { CardGrid, SelectCardWithToggle, Button, Spinner, ConfirmationModal } from '@crc-evans/ui-components';
import { message } from 'antd';
import axiosConfig from '../../services/axiosConfig';
import DPRSettingsTeamsTab from './DPRSettingsTeamsTab';
import DPRSettingsReportingEmailTab from './DPRSettingsReportingEmailTab';
import DPRSettingsConsumablesTab from './DPRSettingsConsumablesTab';
import DPRSettingsPipelinesTab from './DPRSettingsPipelinesTab';
import './DPRSettings.css';


// Internal Tabs component since it's not exported from the library yet
const Tabs = ({ items, activeTabId, onTabClick }) => {
    return (
        <div className="dpr-tabs">
            {items.map(item => (
                <div
                    key={item.id}
                    className={`dpr-tab-item ${activeTabId === item.id ? 'active' : ''}`}
                    onClick={() => onTabClick(item.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    {item.icon != null ? item.icon : null}
                    {item.label}
                </div>
            ))}
        </div>
    );
};

// Role select dropdown – same styling as other DPR dropdowns, opens downward, scrollable list
const RoleSelect = ({ value, options = [], onChange, placeholder = 'Select or enter role' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const selected = options.find((o) => Number(o.id) === Number(value));
    const displayLabel = selected ? selected.name : '';

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%', minWidth: 0 }}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    width: '100%',
                    minHeight: '38px',
                    padding: '8px 36px 8px 12px',
                    fontSize: '14px',
                    border: isOpen ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    color: displayLabel ? '#374151' : '#9CA3AF',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                }}
            >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {displayLabel || placeholder}
                </span>
                {isOpen ? <ChevronUp size={18} color="#6B7280" /> : <ChevronDown size={18} color="#6B7280" />}
            </div>
            {isOpen && (
                <div
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        zIndex: 9999,
                        backgroundColor: '#fff',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        padding: '6px 0',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }}
                >
                    {options.map((opt) => (
                        <div
                            key={opt.id}
                            onClick={() => { onChange(opt.id); setIsOpen(false); }}
                            style={{
                                padding: '8px 12px',
                                fontSize: '14px',
                                color: '#374151',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                backgroundColor: Number(value) === Number(opt.id) ? '#F1F5F9' : 'transparent'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F8FAFC'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = Number(value) === Number(opt.id) ? '#F1F5F9' : 'transparent'; }}
                        >
                            {opt.name}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const CONFIG_KEYS = [
    'includeSummary', 'includeSafety', 'includeQuality', 'includeProductivity',
    'includePersonnel', 'includeComments', 'includeDowntime', 'includeConsumables',
    'generateReportAfterSubmission'
];

/** Normalize API value to 1 or 0 for toggle. GET response: 1 = on, 0 = off. */
const toOneOrZero = (val) => (val === 1 || val === true || val === '1') ? 1 : 0;

/** Normalize GET response so we have a flat object with 1/0 for each key. */
const normalizeReportConfig = (raw) => {
    const data = raw?.data != null ? raw.data : raw;
    if (!data || typeof data !== 'object') return null;
    const config = {};
    CONFIG_KEYS.forEach((key) => {
        config[key] = toOneOrZero(data[key]);
    });
    return { ...data, ...config };
};

const DPRSettings = ({
    activeTab,
    setActiveTab,
    projectId,
    selectedProject
}) => {
    const [reportConfig, setReportConfig] = useState(null);
    const [defectCodes, setDefectCodes] = useState([
        { id: 1, code: 'Ist', description: 'listen' }
    ]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isAddCodeModalOpen, setIsAddCodeModalOpen] = useState(false);
    const [newDefectCode, setNewDefectCode] = useState({ code: '', description: '' });
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [defectToDelete, setDefectToDelete] = useState(null);

    // Roles & Shift: roleOptions from GET /role; plan rows { id, roleId, roleTitle, dayHours, nightHours }
    const [roleOptions, setRoleOptions] = useState([]);
    const [roles, setRoles] = useState([]);
    const [isLoadingRolesShift, setIsLoadingRolesShift] = useState(false);

    // Fetch Report Config
    const fetchReportConfig = async () => {
        setIsLoading(true);
        try {
            const response = await axiosConfig.get(`/dpr-report-config?projectId=${projectId}`);
            const normalized = normalizeReportConfig(response.data);
            setReportConfig(normalized || null);
        } catch (error) {
            console.error('Error fetching report config:', error);
            message.error('Failed to load report configuration');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchDefectTypes = async () => {
        setIsLoading(true);
        try {
            const response = await axiosConfig.get(`/dpr-defect-type?projectId=${projectId}`);
            const data = response.data?.data?.projectLevel || [];
            // Map to state shape if needed, or usually it matches { id, code, description }
            setDefectCodes(data);
        } catch (error) {
            console.error('Error fetching defect types:', error);
            message.error('Failed to load defect types');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchRolesAndPlan = async () => {
        if (!projectId) return;
        setIsLoadingRolesShift(true);
        try {
            const roleRes = await axiosConfig.get('/role');
            const rawRoles = roleRes.data?.data ?? roleRes.data;
            const list = Array.isArray(rawRoles)
                ? rawRoles
                : (rawRoles?.rows ?? rawRoles?.roles ?? []);
            const options = (Array.isArray(list) ? list : []).map((item) => ({
                id: item.id ?? item.roleId,
                name: (item.name ?? item.roleName ?? item.title ?? '').trim() || String(item.id ?? item.roleId ?? '')
            })).filter((item) => item.id != null);
            setRoleOptions(options);

            const planRes = await axiosConfig.get(`/project-role-shift-plan?projectId=${projectId}`);
            const rawPlan = planRes.data?.data ?? planRes.data;
            const planList = Array.isArray(rawPlan?.roles) ? rawPlan.roles : (Array.isArray(rawPlan) ? rawPlan : []);
            const getRoleName = (roleId) => options.find((o) => Number(o.id) === Number(roleId))?.name ?? '';
            const rows = planList.map((item, index) => {
                const roleId = item.roleId ?? item.id;
                return {
                    id: `plan-${index}-${roleId}-${Date.now()}`,
                    roleId: roleId,
                    roleTitle: getRoleName(roleId) || item.roleName || item.name || '',
                    dayHours: String(item.dayPlannedHours ?? item.dayHours ?? 12),
                    nightHours: String(item.nightPlannedHours ?? item.nightHours ?? 12)
                };
            });
            setRoles(rows);
        } catch (error) {
            console.error('Error fetching roles / plan:', error);
            message.error('Failed to load roles and shift plan');
            setRoleOptions([]);
            setRoles([]);
        } finally {
            setIsLoadingRolesShift(false);
        }
    };

    useEffect(() => {
        if (!projectId) return;
        if (activeTab === 'reportConfig') {
            fetchReportConfig();
        } else if (activeTab === 'defectTypes') {
            fetchDefectTypes();
        } else if (activeTab === 'rolesShift') {
            fetchRolesAndPlan();
        }
    }, [projectId, activeTab]);

    const addRole = () => {
        const newId = `plan-new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setRoles([...roles, { id: newId, roleId: '', roleTitle: '', dayHours: '12', nightHours: '12' }]);
    };

    const updateRole = (rowId, field, value) => {
        if (field === 'roleId') {
            const opt = roleOptions.find((o) => Number(o.id) === Number(value));
            setRoles(roles.map((r) => r.id === rowId ? { ...r, roleId: value, roleTitle: opt ? opt.name : r.roleTitle } : r));
        } else {
            setRoles(roles.map((r) => r.id === rowId ? { ...r, [field]: value } : r));
        }
    };

    const removeRole = (id) => {
        setRoles(roles.filter((r) => r.id !== id));
    };

    const handleSaveRolesShift = async () => {
        if (!projectId) return;
        const validRows = roles.filter((r) => r.roleId != null && r.roleId !== '');
        if (validRows.length === 0) {
            message.warning('Add at least one role with a selected role.');
            return;
        }
        setIsSaving(true);
        try {
            const payload = {
                projectId: Number(projectId),
                roles: validRows.map((r) => ({
                    roleId: Number(r.roleId),
                    dayPlannedHours: parseFloat(r.dayHours) || 0,
                    nightPlannedHours: parseFloat(r.nightHours) || 0
                }))
            };
            await axiosConfig.put('/project-role-shift-plan', payload);
            message.success('Role shift plan saved successfully.');
        } catch (error) {
            console.error('Error saving project role shift plan:', error);
            message.error('Failed to save role shift plan.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggle = (key, value) => {
        if (!reportConfig) return;

        const newValue = value ? 1 : 0;
        setReportConfig(prev => ({
            ...prev,
            [key]: newValue
        }));
    };

    const handleOpenAddCodeModal = () => {
        setNewDefectCode({ code: '', description: '' });
        setIsAddCodeModalOpen(true);
    };

    const handleCloseAddCodeModal = () => {
        setIsAddCodeModalOpen(false);
        setNewDefectCode({ code: '', description: '' });
    };

    const handleConfirmAddCode = () => {
        const code = (newDefectCode.code || '').trim();
        if (!code) return;
        const newId = defectCodes.length > 0 ? Math.max(...defectCodes.map(d => d.id)) + 1 : 1;
        setDefectCodes([...defectCodes, { id: newId, code, description: (newDefectCode.description || '').trim() }]);
        handleCloseAddCodeModal();
    };

    const handleDeleteDefectCode = (id) => {
        setDefectToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDeleteDefectCode = async () => {
        if (!defectToDelete) return;

        const updatedCodes = defectCodes.filter(d => d.id !== defectToDelete);
        setDefectCodes(updatedCodes); // Optimistic update
        setIsDeleteModalOpen(false);
        setDefectToDelete(null);

        // Call API to save changes immediately
        setIsSaving(true);
        try {
            const payload = {
                projectId,
                defectTypes: updatedCodes.map(({ code, description }) => ({ code, description }))
            };
            await axiosConfig.put('/dpr-defect-type', payload);
            message.success('Defect type deleted successfully');
            await fetchDefectTypes();
        } catch (error) {
            console.error('Error deleting defect type:', error);
            message.error('Failed to delete defect type');
            await fetchDefectTypes(); // Revert on error
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancelDelete = () => {
        setIsDeleteModalOpen(false);
        setDefectToDelete(null);
    };

    const handleUpdateDefectCode = (id, field, value) => {
        setDefectCodes(defectCodes.map(d =>
            d.id === id ? { ...d, [field]: value } : d
        ));
    };

    /** Build API payload: all config keys as 1 or 0, plus projectId. */
    const buildPayload = (config) => {
        const payload = { projectId };
        CONFIG_KEYS.forEach((key) => {
            payload[key] = toOneOrZero(config?.[key]);
        });
        return payload;
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            if (activeTab === 'reportConfig') {
                if (!reportConfig) return;
                await axiosConfig.put('/dpr-report-config', buildPayload(reportConfig));
                message.success('Configuration saved successfully');
                await fetchReportConfig();
            } else if (activeTab === 'defectTypes') {
                const payload = {
                    projectId,
                    defectTypes: defectCodes.map(({ code, description }) => ({ code, description }))
                };
                await axiosConfig.put('/dpr-defect-type', payload);
                message.success('Defect types saved successfully');
                await fetchDefectTypes();
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            message.error('Failed to save settings');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="dpr-settings-panel">
            <div className="dpr-report-tabs-container" style={{ marginTop: '32px' }}>
                <Tabs
                    items={[
                        { label: 'Pipelines', id: 'pipelines', icon: <GitBranch size={18} /> },   
                        { label: 'Reporting Email', id: 'reportingEmail', icon: <Mail size={18} /> },
                        { label: 'Teams', id: 'team', icon: <Users size={18} /> },
                        { label: 'Consumables', id: 'consumables', icon: <Package size={18} /> },

                        { label: 'Defect Types', id: 'defectTypes' },
                        { label: 'Roles & Shift', id: 'rolesShift' },
                        { label: 'Report Config', id: 'reportConfig' },
                    ]}
                    activeTabId={activeTab}
                    onTabClick={setActiveTab}
                />
            </div>
            <div className="dpr-settings-content">
                {activeTab === 'reportConfig' && (
                    <div className="dpr-settings-tab-content">
                        {isLoading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                                <Spinner size="medium" />
                            </div>
                        ) : reportConfig ? (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div className="dpr-settings-card-container">
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                                        <div style={{ marginRight: '16px', color: '#3B82F6', backgroundColor: '#EFF6FF', padding: '8px', borderRadius: '50%', display: 'flex' }}>
                                            <Info size={24} />
                                        </div>
                                        <div style={{ textAlign: 'left' }}>
                                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#0F172A' }}>Report Configuration</h3>
                                            <p style={{ margin: 0, color: '#64748B', fontSize: '14px' }}>Configure which sections appear in the DPR report.</p>
                                        </div>
                                    </div>
                                    <CardGrid columns={2}>
                                        <SelectCardWithToggle
                                            title="Summary"
                                            checked={reportConfig.includeSummary === 1}
                                            onToggle={(checked) => handleToggle('includeSummary', checked)}
                                        />
                                        <SelectCardWithToggle
                                            title="Safety"
                                            checked={reportConfig.includeSafety === 1}
                                            onToggle={(checked) => handleToggle('includeSafety', checked)}
                                        />
                                        <SelectCardWithToggle
                                            title="Quality"
                                            checked={reportConfig.includeQuality === 1}
                                            onToggle={(checked) => handleToggle('includeQuality', checked)}
                                        />
                                        <SelectCardWithToggle
                                            title="Productivity"
                                            checked={reportConfig.includeProductivity === 1}
                                            onToggle={(checked) => handleToggle('includeProductivity', checked)}
                                        />
                                        <SelectCardWithToggle
                                            title="Personnel"
                                            checked={reportConfig.includePersonnel === 1}
                                            onToggle={(checked) => handleToggle('includePersonnel', checked)}
                                        />
                                        <SelectCardWithToggle
                                            title="Comments"
                                            checked={reportConfig.includeComments === 1}
                                            onToggle={(checked) => handleToggle('includeComments', checked)}
                                        />
                                        <SelectCardWithToggle
                                            title="Downtime"
                                            checked={reportConfig.includeDowntime === 1}
                                            onToggle={(checked) => handleToggle('includeDowntime', checked)}
                                        />
                                        <SelectCardWithToggle
                                            title="Consumables"
                                            checked={reportConfig.includeConsumables === 1}
                                            onToggle={(checked) => handleToggle('includeConsumables', checked)}
                                        />
                                        <SelectCardWithToggle
                                            title="Generate Report After Submission"
                                            checked={reportConfig.generateReportAfterSubmission === 1}
                                            onToggle={(checked) => handleToggle('generateReportAfterSubmission', checked)}
                                        />
                                    </CardGrid>

                                    <div className="dpr-settings-footer">
                                        <Button
                                            label={isSaving ? "Saving..." : "Save"}
                                            variant="secondary"
                                            onClick={handleSave}
                                            disabled={isSaving}
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ color: '#64748B' }}>No configuration found.</div>
                        )}
                    </div>
                )}
                {activeTab === 'defectTypes' && (
                    <div className="dpr-settings-tab-content">
                        {isLoading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                                <Spinner size="medium" />
                            </div>
                        ) : (
                            <div className="dpr-settings-card-container">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                                    <div style={{ textAlign: 'left' }}>
                                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#0F172A' }}>Defect Codes</h3>
                                        <p style={{ margin: 0, color: '#64748B', fontSize: '14px' }}>Configure defect codes for the Weld Log.</p>
                                    </div>
                                    <Button
                                        label="Add Code"
                                        variant="primary"
                                        size="medium"
                                        icon={Plus}
                                        onClick={handleOpenAddCodeModal}
                                    />
                                </div>


                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <CardGrid columns={2}>
                                        {defectCodes.map((defect) => (
                                            <div key={defect.id} className="dpr-defect-type-card">
                                                <input
                                                    type="text"
                                                    className="dpr-defect-code"
                                                    value={defect.code}
                                                    onChange={(e) => handleUpdateDefectCode(defect.id, 'code', e.target.value)}
                                                    placeholder="Code"
                                                />
                                                <input
                                                    type="text"
                                                    className="dpr-defect-description"
                                                    value={defect.description}
                                                    onChange={(e) => handleUpdateDefectCode(defect.id, 'description', e.target.value)}
                                                    placeholder="Description"
                                                />
                                                <button
                                                    type="button"
                                                    className="dpr-defect-delete"
                                                    onClick={() => handleDeleteDefectCode(defect.id)}
                                                    aria-label="Delete defect code"
                                                >
                                                    <Trash size={18} />
                                                </button>
                                            </div>
                                        ))}
                                    </CardGrid>
                                    {defectCodes.length === 0 && (
                                        <div style={{ textAlign: 'center', padding: '40px', color: '#94A3B8' }}>
                                            No defect codes configured. Add one to get started.
                                        </div>
                                    )}
                                </div>

                                <div className="dpr-settings-footer">
                                    <Button
                                        label={isSaving ? "Saving..." : "Save"}
                                        variant="secondary"
                                        onClick={handleSave}
                                        disabled={isSaving}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {activeTab === 'rolesShift' && (
                    <div className="dpr-settings-tab-content">
                        {isLoadingRolesShift ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                                <Spinner size="medium" />
                            </div>
                        ) : (
                            <div className="dpr-settings-card-container">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                                    <div style={{ textAlign: 'left' }}>
                                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#0F172A' }}>Project Roles & Shift Plan</h3>
                                        <p style={{ margin: '8px 0 0 0', color: '#64748B', fontSize: '14px' }}>Define roles and their planned hours per shift.</p>
                                    </div>
                                    <Button
                                        label="Add Role"
                                        variant="primary"
                                        size="medium"
                                        icon={Plus}
                                        onClick={addRole}
                                    />
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    {/* Table header - widths match row columns for alignment */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '12px 0',
                                        borderBottom: '1px solid #E2E8F0',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: '#64748B',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        gap: '12px'
                                    }}>
                                        <div style={{ flex: '1 1 45%', minWidth: '220px', textAlign: 'left' }}>ROLE TITLE</div>
                                        <div style={{ width: '150px', flexShrink: 0, textAlign: 'center' }}>DAY HOURS</div>
                                        <div style={{ width: '150px', flexShrink: 0, textAlign: 'center' }}>NIGHT HOURS</div>
                                        <div style={{ width: '56px', flexShrink: 0, textAlign: 'center' }}>ACTION</div>
                                    </div>

                                    {roles.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '32px', color: '#94A3B8', fontSize: '14px' }}>
                                            No roles added. Click &quot;Add Role&quot; to define roles and shift hours.
                                        </div>
                                    ) : (
                                        roles.map((role) => (
                                            <div
                                                key={role.id}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '12px 0',
                                                    borderBottom: '1px solid #F1F5F9',
                                                    gap: '12px'
                                                }}
                                            >
                                                <div style={{ flex: '1 1 45%', minWidth: '220px' }}>
                                                    <RoleSelect
                                                        value={role.roleId ?? ''}
                                                        options={roleOptions}
                                                        onChange={(val) => updateRole(role.id, 'roleId', val)}
                                                        placeholder="Select or enter role"
                                                    />
                                                </div>
                                                <div style={{ width: '150px', flexShrink: 0 }}>
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        step={0.5}
                                                        value={role.dayHours ?? ''}
                                                        onChange={(e) => updateRole(role.id, 'dayHours', e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '10px 12px',
                                                            borderRadius: '6px',
                                                            border: '1px solid #E2E8F0',
                                                            fontSize: '14px',
                                                            outline: 'none',
                                                            color: '#1F2937',
                                                            boxSizing: 'border-box',
                                                            textAlign: 'center'
                                                        }}
                                                        onFocus={(e) => { e.target.style.borderColor = '#3B82F6'; }}
                                                        onBlur={(e) => { e.target.style.borderColor = '#E2E8F0'; }}
                                                    />
                                                </div>
                                                <div style={{ width: '150px', flexShrink: 0 }}>
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        step={0.5}
                                                        value={role.nightHours ?? ''}
                                                        onChange={(e) => updateRole(role.id, 'nightHours', e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '10px 12px',
                                                            borderRadius: '6px',
                                                            border: '1px solid #E2E8F0',
                                                            fontSize: '14px',
                                                            outline: 'none',
                                                            color: '#1F2937',
                                                            boxSizing: 'border-box',
                                                            textAlign: 'center'
                                                        }}
                                                        onFocus={(e) => { e.target.style.borderColor = '#3B82F6'; }}
                                                        onBlur={(e) => { e.target.style.borderColor = '#E2E8F0'; }}
                                                    />
                                                </div>
                                                <div style={{ width: '56px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeRole(role.id)}
                                                        aria-label="Delete role"
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            color: '#94A3B8',
                                                            padding: '6px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        onMouseEnter={(e) => { e.currentTarget.style.color = '#EF4444'; }}
                                                        onMouseLeave={(e) => { e.currentTarget.style.color = '#94A3B8'; }}
                                                    >
                                                        <Trash size={18} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>

                                {roles.length > 0 && (
                                    <div className="dpr-settings-footer">
                                        <Button
                                            label={isSaving ? 'Saving...' : 'Save'}
                                            variant="secondary"
                                            onClick={handleSaveRolesShift}
                                            disabled={isSaving}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {activeTab === 'team' && (
                    <div className="dpr-settings-tab-content dpr-settings-team-tab">
                        {selectedProject ? (
                            <DPRSettingsTeamsTab selectedProject={selectedProject} />
                        ) : (
                            <div className="dpr-settings-card-container">
                                <p style={{ margin: 0, color: '#64748B' }}>
                                    Select a project to manage team members.
                                </p>
                            </div>
                        )}
                    </div>
                )}
                {activeTab === 'reportingEmail' && (
                    <div className="dpr-settings-tab-content">
                        <DPRSettingsReportingEmailTab selectedProject={selectedProject} />
                    </div>
                )}
                {activeTab === 'consumables' && (
                    <div className="dpr-settings-tab-content">
                        <DPRSettingsConsumablesTab selectedProject={selectedProject} />
                    </div>
                )}
                {activeTab === 'pipelines' && (
                    <div className="dpr-settings-tab-content">
                        <DPRSettingsPipelinesTab selectedProject={selectedProject} />
                    </div>
                )}
            </div>

            {/* Add Defect Code Modal - same styling as Safety / Quality modals */}
            {isAddCodeModalOpen && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    paddingTop: '100px',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: '12px',
                        width: '500px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}>
                        {/* Header */}
                        <div style={{
                            padding: '24px',
                            borderBottom: '1px solid #E5E7EB',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start'
                        }}>
                            <div style={{ display: 'flex', gap: '16px' }}>
                                <div style={{
                                    backgroundColor: '#EEF2FF',
                                    padding: '10px',
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#4F46E5'
                                }}>
                                    <ListChecks size={24} />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                                        Add Defect Code
                                    </h3>
                                    <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
                                        Add code and description for the Weld Log.
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleCloseAddCodeModal}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#9CA3AF',
                                    padding: '4px'
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '6px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#374151',
                                    textAlign: 'left'
                                }}>
                                    Code <span style={{ color: '#EF4444' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g., CRK"
                                    value={newDefectCode.code}
                                    onChange={(e) => setNewDefectCode({ ...newDefectCode, code: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid #D1D5DB',
                                        fontSize: '14px',
                                        outline: 'none',
                                        color: '#1F2937',
                                        boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => { e.target.style.borderColor = '#4F46E5'; }}
                                    onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; }}
                                />
                            </div>
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '6px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#374151',
                                    textAlign: 'left'
                                }}>
                                    Description
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g., Crack"
                                    value={newDefectCode.description}
                                    onChange={(e) => setNewDefectCode({ ...newDefectCode, description: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid #D1D5DB',
                                        fontSize: '14px',
                                        outline: 'none',
                                        color: '#1F2937',
                                        boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => { e.target.style.borderColor = '#4F46E5'; }}
                                    onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; }}
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding: '16px 24px',
                            backgroundColor: '#F9FAFB',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '12px',
                            borderTop: '1px solid #E5E7EB'
                        }}>
                            <Button
                                label="Cancel"
                                variant="tertiary"
                                onClick={handleCloseAddCodeModal}
                                size="medium"
                            />
                            <Button
                                label="Add Code"
                                variant="primary"
                                onClick={handleConfirmAddCode}
                                disabled={!(newDefectCode.code || '').trim()}
                                size="medium"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                title="Delete Defect Type"
                message="Are you sure you want to delete this defect type? This action cannot be undone."
                confirmText="Yes, Delete"
                cancelText="Cancel"
                onConfirm={handleConfirmDeleteDefectCode}
                onCancel={handleCancelDelete}
                isOpen={isDeleteModalOpen}
                size="medium"
                confirmButtonProps={{
                    style: {
                        backgroundColor: '#EF4444',
                        borderColor: '#EF4444'
                    }
                }}
            />
        </div>
    );
};

export default DPRSettings;
